import { useEffect, useRef } from "react";
import { bindAnimPause } from "./animPause";

/*
 * Wavefield cube — the People figure, after the engraved-cube reference:
 * an isometric cube whose three visible faces each carry a vortex of
 * concentric rings around a dark eye. Rings live in face coordinates and
 * clip at the face edges, so the perspective shear falls out of the face
 * basis. They drift slowly outward from the eyes; the eyes breathe.
 */

const CW = 780;
const CH = 510;

/* isometric basis, edge length s */
const S = 216;
const X: [number, number] = [0.866 * S, 0.5 * S];
const Y: [number, number] = [-0.866 * S, 0.5 * S];
const NZ: [number, number] = [0, S]; /* -Z: straight down */

const CXc = CW * 0.52;
const CYc = CH * 0.5 - S * 0.02;
const T0: [number, number] = [CXc, CYc - S];

interface Face {
  o: [number, number]; /* origin corner */
  U: [number, number];
  V: [number, number];
  eye: [number, number]; /* vortex center in face coords */
  hole: number; /* the dark eye radius, face units */
  w: number; /* strength */
  drift: number; /* ring drift speed */
}

const add = (a: [number, number], b: [number, number]): [number, number] =>
  [a[0] + b[0], a[1] + b[1]];

const FACES: Face[] = [
  /* top: eye toward the upper-left area, like the reference */
  { o: T0, U: X, V: Y, eye: [0.6, 0.34], hole: 0.055, w: 1, drift: 0.00042 },
  /* left face */
  { o: add(T0, Y), U: X, V: NZ, eye: [0.1, 0.26], hole: 0.05, w: 0.85, drift: 0.00034 },
  /* right face: the big eye, lower-right */
  { o: add(T0, X), U: Y, V: NZ, eye: [0.32, 0.62], hole: 0.11, w: 0.95, drift: 0.00038 },
];

const rnd = (i: number) => {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
};

export default function WavefieldArt() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cnv = ref.current;
    if (!cnv) return;
    const g = cnv.getContext("2d");
    if (!g) return;

    const CS = getComputedStyle(document.documentElement);
    const C = (n: string) => CS.getPropertyValue(n).trim();
    const rgb = (n: string): [number, number, number] => {
      let h = C(n).replace("#", "");
      if (h.length === 3) h = h.split("").map((x) => x + x).join("");
      const v = parseInt(h, 16);
      return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
    };
    const MIST = rgb("--mist");
    const CYAN = rgb("--cyan");
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    /* dense bright rings at the eye, fading into mist outward */
    const tone = (age: number, a: number) => {
      const r = lerp(CYAN[0], MIST[0], age);
      const gr = lerp(CYAN[1], MIST[1], age);
      const b = lerp(CYAN[2], MIST[2], age);
      return `rgba(${r | 0},${gr | 0},${b | 0},${a})`;
    };
    const RM = matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0, H = 0, D = 1;
    function size() {
      D = Math.min(devicePixelRatio || 1, 2.5);
      const r = cnv!.getBoundingClientRect();
      W = Math.max(1, r.width);
      H = Math.max(1, r.height);
      cnv!.width = W * D;
      cnv!.height = H * D;
      g!.setTransform((D * W) / CW, 0, 0, (D * H) / CH, 0, 0);
    }

    const P = (f: Face, u: number, v: number): [number, number] => [
      f.o[0] + u * f.U[0] + v * f.V[0],
      f.o[1] + u * f.U[1] + v * f.V[1],
    ];

    const RMAX = 1.5; /* rings die once fully outside the face anyway */
    const N_RINGS = 34;
    const STEPS = 110;

    const drawFrame = (tick: number) => {
      g!.clearRect(0, 0, CW, CH);

      FACES.forEach((f, fi) => {
        const breathe = 0.5 + 0.5 * Math.sin(tick / 120 + fi * 2.1);
        const hole = f.hole * (1 + 0.14 * breathe);
        for (let k = 0; k < N_RINGS; k++) {
          /* rings drift outward: phase cycles 0..1 across the ring set */
          const ph = ((k / N_RINGS + tick * f.drift) % 1 + 1) % 1;
          /* spacing tightens near the eye, opens outward */
          const r = hole + Math.pow(ph, 1.35) * (RMAX - hole);
          const age = ph;
          const a =
            f.w *
            (0.48 * Math.pow(1 - age, 1.2) + 0.12) *
            Math.min(1, (r - hole) / 0.02 + 0.25);
          if (a <= 0.012) continue;
          const wob = rnd(fi * 53 + k * 17) * 0.008;
          g!.strokeStyle = tone(age, a);
          g!.lineWidth = 0.9 - 0.35 * age;
          g!.beginPath();
          let on = false;
          for (let q = 0; q <= STEPS; q++) {
            const th = (q / STEPS) * Math.PI * 2;
            const u = f.eye[0] + (r + wob * Math.sin(th * 3 + k)) * Math.cos(th);
            const v = f.eye[1] + (r + wob * Math.cos(th * 2 + k)) * Math.sin(th);
            if (u < 0.015 || u > 0.985 || v < 0.015 || v > 0.985) {
              on = false;
              continue;
            }
            const [x, y] = P(f, u, v);
            if (!on) { g!.moveTo(x, y); on = true; } else g!.lineTo(x, y);
          }
          g!.stroke();
        }
      });
    };

    let tick = 0;
    let rafLoop = 0, rafSize = 0;
    function frame() {
      tick++;
      drawFrame(tick);
      if (!RM) rafLoop = requestAnimationFrame(frame);
    }

    /* phone URL-bar show/hide fires height-only resizes; the figure box
       is width-driven there, and a rebuild blanks the canvas for a frame */
    let lastArtW = window.innerWidth;
    const phoneMQ = window.matchMedia("(max-width: 768px)");
    const onResize = () => {
      if (phoneMQ.matches && window.innerWidth === lastArtW) return;
      lastArtW = window.innerWidth;
      cancelAnimationFrame(rafSize);
      rafSize = requestAnimationFrame(() => {
        size();
        if (!RM) {
          cancelAnimationFrame(rafLoop);
          rafLoop = requestAnimationFrame(frame);
        } else drawFrame(tick);
      });
    };
    window.addEventListener("resize", onResize);
    /* the canvas can mount before its column has settled its final size —
       track the element itself, not just the window */
    const ro = new ResizeObserver(onResize);
    ro.observe(cnv);

    size();
    if (RM) drawFrame(tick);
    else frame();

    const unbind = RM ? null : bindAnimPause(
      cnv,
      () => { cancelAnimationFrame(rafLoop); rafLoop = requestAnimationFrame(frame); },
      () => cancelAnimationFrame(rafLoop)
    );

    return () => {
      cancelAnimationFrame(rafLoop);
      if (unbind) unbind();
      cancelAnimationFrame(rafSize);
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div className="team-airflow-art">
      <canvas ref={ref} aria-hidden="true" />
    </div>
  );
}
